import { ConfigService } from '@stockcare-services/config-service/config.service';
import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Subject, interval, Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AnalyticsNames } from '@stockcare-helpers/analytics-names';
import { PerformanceMetricService } from '@stockcare-services/performance-metric-service/performance-metric-service';
import { QueueStorageService } from '@stockcare-storage/queue/queue-storage.service';
import { QueueItem } from '@stockcare-storage/queue/queue';
import { QueueType } from '@stockcare-models/QueueType';
import { QueueStatus } from '@stockcare-models/QueueStatus';
import { UUID } from 'angular2-uuid';
import { IHealthInformation } from '@stockcare-models/IHealthInformation';
import { AuthenticationService } from '..';

@Injectable({
  providedIn: 'root',
})
export class QueueService implements OnDestroy {
  private interval = new Subject();
  backoffInterval = 0;
  alreadyDequeued = false;
  lastRunDateTime: Date;
  lastSuccessDateTime: Date;
  lastFailureDateTime: Date;
  failureCountToday: number = 0;
  successCountToday: number = 0;
  firstRunDateTime: Date;
  pendingBasketsCount: number = 0;
  subscriptions = new Subscription();

  constructor(
    private http: HttpClient,
    private queueStorageService: QueueStorageService,
    private configuration: ConfigService,
    private performanceMetricService: PerformanceMetricService,
    private authService: AuthenticationService
  ) {}

  initialize() {
    console.log('Starting queueing service');
    this.start();

    this.interval.next(this.backoffInterval);
    this.firstRunDateTime = new Date();
  }

  enqueue<T>(queueType: QueueType, value: T): Observable<boolean> {
    const userId = this.authService.getUserId();

    return new Observable((observer) => {
      const queueItem = new QueueItem();
      queueItem.DateCreated = new Date();
      queueItem.Status = QueueStatus.New;
      queueItem.Type = queueType;
      queueItem.Value = JSON.stringify(value);
      queueItem.CorrelationId = UUID.UUID();
      queueItem.RetryCount = 0;
      queueItem.UserId = userId;
      queueItem.IsOffline = false;

      this.processFirstQueueItemAttempt(queueItem).subscribe(
        () => observer.next(),
        () => observer.error()
      );
    });
  }

  checkHealth(): IHealthInformation {
    return {
      name: "Queue Polling Service",
      firstRunDateTime: this.firstRunDateTime,
      lastRunDateTime: this.lastRunDateTime,
      lastSuccessDateTime: this.lastSuccessDateTime,
      lastFailureDateTime: this.lastFailureDateTime,
      successCountToday: this.successCountToday,
      failureCountToday: this.failureCountToday,
      isHealthy: this.pendingBasketsCount === 0,
    };
  }

  processTheatreQueueWhenDeviceComesOnline() {
    this.processQueue();
  }

  private start() {
    this.subscriptions.add(
      this.interval
        .pipe(switchMap((intervalSetting: number) => interval(intervalSetting)))
        .subscribe(() => this.processQueue())
    );
  }

  private processQueue() {
    if (
      this.lastRunDateTime &&
      this.lastRunDateTime.toDateString() !== new Date().toDateString()
    ) {
      this.successCountToday = 0;
      this.failureCountToday = 0;
    }

    this.lastRunDateTime = new Date();

    this.queueStorageService.getPendingCount().subscribe((count) => {
      this.pendingBasketsCount = count;
    });

    if (this.alreadyDequeued) {
      return;
    }

    this.alreadyDequeued = true;
    this.queueStorageService.dequeue().subscribe((queueItem) => {
      if (!queueItem) {
        this.alreadyDequeued = false;
        this.backoff();
        return;
      }

      this.processQueueItem(queueItem);
    });
  }

  private backoff() {
    if (this.backoffInterval >= 15000) {
      return;
    }

    this.backoffInterval += 5000;
    this.interval.next(this.backoffInterval);
  }

  private processQueueItem(queueItem: QueueItem) {
    const startTime = new Date();
    this.http
      .post<any[]>(`${this.configuration.config.baseUrl}/queue`, queueItem)
      .subscribe(
        (result) => {
          this.performanceMetricService.buildAndSubmitMetrics(
            startTime,
            AnalyticsNames.submitBasket
          );

          this.queueStorageService.complete(queueItem.Id).subscribe(() => {
            this.alreadyDequeued = false;
            this.backoffInterval = 5000;
            this.successCountToday++;
            this.lastSuccessDateTime = new Date();
          });
        },
        (error) => {
          this.failureCountToday++;
          this.lastFailureDateTime = new Date();
          queueItem.ServerResponse = JSON.stringify(
            error.statusCode + "-" + error.statusText
          );
          queueItem.IsOffline = true;
          this.queueStorageService.retry(queueItem.Id, error).subscribe(() => {
            this.alreadyDequeued = false;
            this.backoffInterval = 5000;
          });
        }
      );
  }

  private processFirstQueueItemAttempt(queueItem: QueueItem) {
    return new Observable((observer) => {
      const startTime = new Date();
      this.http
        .post<any[]>(`${this.configuration.config.baseUrl}/queue`, queueItem)
        .subscribe(
          (result) => {
            this.performanceMetricService.buildAndSubmitMetrics(
              startTime,
              AnalyticsNames.submitQueueItem
            );
            this.successCountToday++;
            this.lastSuccessDateTime = new Date();
            observer.next();
          },
          (error) => {
            this.failureCountToday++;
            this.lastFailureDateTime = new Date();
            queueItem.ServerResponse = JSON.stringify(
              error.statusCode + "-" + error.statusText
            );
            queueItem.IsOffline = true;
            this.queueStorageService.enqueue(queueItem).subscribe(() => {
              observer.error();
            });
          }
        );
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
